import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { COUNTRIES, DEFAULT_COUNTRY } from "../data/countries";

/**
 * PhoneInput
 *  Composite input: country dial code (with flag + searchable dropdown) +
 *  national phone number. Emits a normalised string via onChange.
 *
 * Props:
 *  - value     : { country: "AZ", dial: "+994", number: "501234567" }
 *  - onChange  : (next) => void
 *  - placeholder
 *  - required
 *  - testid
 *  - lang      : "az" | "ru" | "en" (controls dropdown label language)
 */
export default function PhoneInput({
  value,
  onChange,
  placeholder = "50 123 45 67",
  required = false,
  testid = "phone-input",
  lang = "az",
}) {
  const safeValue = value || { country: DEFAULT_COUNTRY.code, dial: DEFAULT_COUNTRY.dial, number: "" };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const selected = COUNTRIES.find((c) => c.code === safeValue.country) || DEFAULT_COUNTRY;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) =>
      [c.name, c.nameAz, c.dial, c.code]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q))
    );
  }, [search]);

  const selectCountry = (c) => {
    onChange?.({ country: c.code, dial: c.dial, number: safeValue.number });
    setOpen(false);
    setSearch("");
  };

  const onNumChange = (e) => {
    // keep digits + spaces only
    const cleaned = e.target.value.replace(/[^\d\s]/g, "");
    onChange?.({ ...safeValue, number: cleaned });
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="flex items-stretch gap-0 bg-transparent border border-[var(--border)] focus-within:border-[var(--primary)] transition-colors">
        {/* Country code button */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 px-3 py-3 border-r border-[var(--border)] hover:bg-black/3 transition-colors flex-shrink-0"
          data-testid={`${testid}-country`}
        >
          <span className="text-xl leading-none">{selected.flag}</span>
          <span className="font-mono text-sm tabular-nums">{selected.dial}</span>
          <ChevronDown size={14} className="text-black/45" />
        </button>

        {/* National number */}
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder={placeholder}
          value={safeValue.number}
          onChange={onNumChange}
          required={required}
          className="flex-1 bg-transparent px-4 py-3 outline-none font-mono text-sm min-w-0"
          data-testid={`${testid}-number`}
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 sm:right-auto sm:w-[340px] bg-white border border-black/10 shadow-xl rounded-md max-h-[340px] flex flex-col">
          {/* Search row */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-black/8">
            <Search size={14} className="text-black/40 flex-shrink-0" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={lang === "ru" ? "Поиск страны..." : lang === "en" ? "Search country..." : "Ölkə axtar..."}
              className="flex-1 bg-transparent outline-none text-sm py-1.5"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} className="text-black/40 hover:text-black/70">
                <X size={14} />
              </button>
            )}
          </div>

          {/* Country list */}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="p-4 text-sm text-black/45 text-center">
                {lang === "ru" ? "Не найдено" : lang === "en" ? "Not found" : "Tapılmadı"}
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  type="button"
                  key={c.code}
                  onClick={() => selectCountry(c)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-black/4 transition-colors text-left ${
                    c.code === selected.code ? "bg-[var(--accent)]/6" : ""
                  }`}
                >
                  <span className="text-xl leading-none flex-shrink-0">{c.flag}</span>
                  <span className="flex-1 text-sm text-[var(--text)] truncate">
                    {lang === "az" ? c.nameAz : c.name}
                  </span>
                  <span className="font-mono text-xs text-black/55 tabular-nums">{c.dial}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
